import React, { useState, useEffect, useRef } from 'react';
import { Container, Box, Typography, Card, CardContent } from '@mui/material';
import CameraConnectionStatus from './components/CameraConnectionStatus';
import Statistics from './components/Statistics';
import LiveEvent from './components/LiveEvent';
import EventHistory from './components/EventHistory';
import { getCameraStatistics, getRecentEvents, getSSEStreamURL } from '../../api/axios';
import './CameraMonitor.css';

const HEARTBEAT_TIMEOUT = 30000; // 30 seconds

/**
 * CameraMonitor Page
 * Main camera monitoring page with SSE connection and multi-camera support
 */
const CameraMonitor = () => {
    const [cameras, setCameras] = useState([]);
    const [statistics, setStatistics] = useState(null);
    const [events, setEvents] = useState([]);
    const [liveEvent, setLiveEvent] = useState(null);
    const [liveEventImage, setLiveEventImage] = useState(null);

    const eventSourceRef = useRef(null);
    const heartbeatCheckIntervalRef = useRef(null);

    // Initialize on mount
    useEffect(() => {
        loadStatistics();
        loadRecentEvents();
        connectToSSE();

        // Start heartbeat checker
        heartbeatCheckIntervalRef.current = setInterval(checkHeartbeats, 5000);

        // Cleanup on unmount
        return () => {
            if (eventSourceRef.current) {
                eventSourceRef.current.close();
            }
            if (heartbeatCheckIntervalRef.current) {
                clearInterval(heartbeatCheckIntervalRef.current);
            }
        };
    }, []);

    /**
     * Load statistics from API
     */
    const loadStatistics = async () => {
        try {
            const data = await getCameraStatistics();
            setStatistics(data);
        } catch (error) {
            console.error('Error loading statistics:', error);
        }
    };

    /**
     * Load recent events from API
     */
    const loadRecentEvents = async () => {
        try {
            const data = await getRecentEvents(50);
            if (Array.isArray(data)) {
                setEvents(data);
            }
        } catch (error) {
            console.error('Error loading events:', error);
        }
    };

    /**
     * Connect to SSE stream
     */
    const connectToSSE = () => {
        const sseURL = getSSEStreamURL();
        console.log('Connecting to SSE:', sseURL);

        const eventSource = new EventSource(sseURL);
        eventSourceRef.current = eventSource;

        // Connection opened
        eventSource.addEventListener('connected', (e) => {
            const data = JSON.parse(e.data);
            console.log('Connected to SSE:', data);
        });

        // New event
        eventSource.addEventListener('event', (e) => {
            const event = JSON.parse(e.data);
            handleNewEvent(event);
        });

        // Realtime event
        eventSource.addEventListener('realtime', (e) => {
            const event = JSON.parse(e.data);
            handleNewEvent(event);
        });

        // Ping/heartbeat
        eventSource.addEventListener('ping', (e) => {
            console.log('Ping received');
        });

        // Error handling
        eventSource.onerror = (err) => {
            console.error('SSE error:', err);
            eventSource.close();

            // Reconnect after 5 seconds
            setTimeout(() => {
                console.log('Reconnecting to SSE...');
                connectToSSE();
            }, 5000);
        };
    };

    /**
     * Handle new event from SSE
     */
    const handleNewEvent = (event) => {
        console.log('New event:', event);

        const eventType = event.type || event.event_type;
        const cameraId = event.camera_id || event.ip || event.ipAddress || 'unknown';

        // Update camera list and heartbeat
        if (eventType === 'heartBeat' || eventType === 'heartbeat') {
            updateCameraHeartbeat(cameraId);
        }

        // Update live event for ANPR
        if (eventType === 'ANPR' || eventType === 'PLATES') {
            setLiveEvent(event);
            setLiveEventImage(event.image_path || event.image);

            // Reload events to get complete data
            setTimeout(() => loadRecentEvents(), 500);
        }

        // Reload statistics
        loadStatistics();
    };

    /**
     * Update camera heartbeat timestamp
     */
    const updateCameraHeartbeat = (cameraId) => {
        setCameras(prevCameras => {
            const existingCamera = prevCameras.find(c => c.id === cameraId);

            if (existingCamera) {
                // Update existing camera
                return prevCameras.map(c =>
                    c.id === cameraId
                        ? { ...c, lastHeartbeat: Date.now(), isConnected: true }
                        : c
                );
            } else {
                // Add new camera
                return [
                    ...prevCameras,
                    {
                        id: cameraId,
                        ip: cameraId,
                        lastHeartbeat: Date.now(),
                        isConnected: true
                    }
                ];
            }
        });
    };

    /**
     * Check heartbeat timeouts for all cameras
     */
    const checkHeartbeats = () => {
        const now = Date.now();

        setCameras(prevCameras =>
            prevCameras.map(camera => ({
                ...camera,
                isConnected: (now - camera.lastHeartbeat) < HEARTBEAT_TIMEOUT
            }))
        );
    };

    return (
        <Container maxWidth="xl" className="camera-monitor-container">
            {/* Header */}
            <Box className="camera-monitor-header">
                <Typography variant="h4" className="page-title">
                    HikVision Camera Monitor
                </Typography>
                <CameraConnectionStatus cameras={cameras} />
            </Box>

            {/* Statistics */}
            <Card className="camera-monitor-card">
                <CardContent>
                    <Typography variant="h4" className="card-title">
                        Statistika
                    </Typography>
                    <Statistics statistics={statistics} />
                </CardContent>
            </Card>

            {/* Live Event */}
            <Card className="camera-monitor-card">
                <CardContent>
                    <Typography variant="h4" className="card-title">
                        Trenutni događaj
                    </Typography>
                    <LiveEvent event={liveEvent} imagePath={liveEventImage} />
                </CardContent>
            </Card>

            {/* Event History */}
            <Card className="camera-monitor-card">
                <CardContent>
                    <Typography variant="h4" className="card-title">
                        Istorija događaja
                    </Typography>
                    <EventHistory events={events} />
                </CardContent>
            </Card>
        </Container>
    );
};

export default CameraMonitor;
